<script setup>
  import { computed, ref, watch } from "vue";
  import { storeToRefs } from "pinia";
  import { LayoutGrid } from "lucide-vue-next";
  import { Button } from "@/components/ui/button";
  import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
  import { AiTextInput } from "@/components/episto-ui/ai";
  import { LabeledField } from "@/components/episto-ui/labeled-field";
  import { useMCreativeStore } from "@stores/mCreative";
  import { useMTemplatesStore } from "@stores/mTemplates";
  import TemplateLibraryModal from "@/components/MCampaign/Creatives/TemplateLibraryModal.vue";

  const isOpen = ref(true);
  const isLibraryOpen = ref(false);

  const { mCreative } = storeToRefs(useMCreativeStore());
  const mTemplatesStore = useMTemplatesStore();
  const { mTemplates, activeMTemplates, availableAttributes, currentAttributes } = storeToRefs(mTemplatesStore);
  const { toggleMTemplate, isMTemplateSelected } = mTemplatesStore;

  const pinnedTemplateIds = ref([]);

  watch(
    () => [mCreative.value, [...(mCreative.value?.template_ids ?? [])]],
    ([creative, selectedIds], [previousCreative] = []) => {
      const keptIds = creative === previousCreative ? pinnedTemplateIds.value : [];
      pinnedTemplateIds.value = [...new Set([...keptIds, ...selectedIds])];
    },
    { immediate: true },
  );

  const pickerTemplates = computed(() =>
    mTemplates.value.filter(
      (el) => (Number.isInteger(el.featured_position) && !el.archived_at) || pinnedTemplateIds.value.includes(el.id),
    ),
  );
</script>

<template>
  <div>
    <Collapsible
      :open="isOpen"
      @update:open="isOpen = $event">
      <CollapsibleTrigger class="flex w-full items-center justify-between py-2 text-sm font-medium">
        Templates
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ul class="inline-flex flex-wrap gap-0 list-none p-0 m-0">
          <li
            v-for="mTemplate in pickerTemplates"
            :key="mTemplate.id"
            class="mr-2 mb-2 inline-flex h-[136px] w-[136px] cursor-pointer overflow-hidden rounded-md border-2 bg-muted"
            :class="isMTemplateSelected(mTemplate.id) ? 'border-primary' : 'border-border'"
            @click="toggleMTemplate(mTemplate.id)">
            <img
              class="block h-full w-full max-w-full object-contain"
              :src="mTemplate.preview_url"
              :alt="mTemplate.name" />
          </li>
        </ul>

        <Button
          variant="outline"
          size="sm"
          class="mb-3"
          @click="isLibraryOpen = true">
          <LayoutGrid class="mr-1 h-4 w-4" />
          Browse all templates
        </Button>

        <div class="space-y-3">
          <LabeledField
            v-for="(att, index) in Object.keys(availableAttributes)"
            :key="`available-attribute-${index}`"
            :label="att">
            <AiTextInput
              v-model="currentAttributes[att]"
              type="textarea"
              :placeholder="availableAttributes[att]?.default" />
          </LabeledField>
        </div>
      </CollapsibleContent>
    </Collapsible>

    <div v-if="!isOpen">
      <ul class="inline-flex flex-wrap gap-0 list-none p-0 m-0">
        <li
          v-for="(mTemplate, index) in activeMTemplates"
          :key="`creative-template-${index}`"
          class="mr-2 mb-2 inline-flex h-[136px] w-[136px] overflow-hidden rounded-md border-2 border-primary bg-muted">
          <img
            class="block h-full w-full max-w-full object-contain"
            :src="mTemplate.preview_url"
            :alt="mTemplate.name" />
        </li>
      </ul>
    </div>

    <TemplateLibraryModal v-model:open="isLibraryOpen" />
  </div>
</template>
